import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { translate } from "../../../../../../common/providers";
import {
	Selector,
	CustomDialog,
	CustomIconButton,
	icon,
	IconComponent,
	CustomTextField,
} from "../../../../../../common/components";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { useApi } from "../../../../../../common/hooks";
import { AiDetectionsService } from "../../../../../../api";
import { COMPLIANCE_STATUS } from "../../constants";
import styles from "./UpdateComplianceDialog.module.css";

type PossibleValue = {
	commitmentCount: number;
	isDeprecated: boolean;
	value: string;
	valueUuuid: string;
	icon?: string;
	[key: string]: unknown;
};

type FieldValue = {
	valueId: string;
	value: string;
};

type FieldPayload = {
	fieldUuid: string;
	fieldName: string;
	value: FieldValue;
};

type Payload = FieldPayload[];

type Compliance = { fieldName: string; fieldUuid: string; possibleValues: PossibleValue[] };

const STATUS_ORDER = [
	{ name: "COMPLIANT", icon: "faCheckCircle" },
	{ name: "PENDING", icon: "faClock" },
	{ name: "NON_COMPLIANT", icon: "faCircleXmark" },
	{ name: "NOT_APPLICABLE", icon: "" },
];

const UpdateComplianceDialog: React.FC<{
	complianceValues: Payload;
	complianceRules: string;
	criteriaId: string;
	open: boolean;
	onClose: () => void;
	onSubmit?: (fieldUuid: string, payload: Payload, newStatus: string, currentStatus: string | undefined) => void;
}> = ({ complianceValues, complianceRules, criteriaId, open, onClose, onSubmit }) => {
	const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
	const { call: updateInnerField } = useApi(AiDetectionsService.updateInnerField);
	const [selectedStatus, setSelectedStatus] = useState<PossibleValue | null>(null);
	const [comment, setComment] = useState<string>(
		complianceValues?.find((cVal) => cVal.fieldName === "complianceComment")?.value?.value ?? ""
	);
	const projectId = useSelector(({ context }) => context.project?.id);
	const [openRules, setOpenRules] = useState<boolean>(false);
	const compliance = useSelector(({ projectSheet }) => projectSheet.innerFields as Compliance[]);
	const complianceStatuses = useMemo(
		() =>
			isNonEmptyArray(compliance)
				? compliance
						?.find(({ fieldName }) => fieldName === "complianceStatus")
						?.possibleValues.slice()
						.sort((a, b) => {
							const aIndex = STATUS_ORDER.findIndex((s) => s.name === a.value);
							const bIndex = STATUS_ORDER.findIndex((s) => s.name === b.value);
							return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
						})
				: [],
		[compliance]
	);
	const disabledSubmit = useMemo(
		() =>
			(!comment.length && selectedStatus?.value?.toUpperCase() !== COMPLIANCE_STATUS.NOT_APPLICABLE) ||
			!selectedStatus ||
			(selectedStatus?.value?.toUpperCase() ===
				complianceValues?.find((val) => val.fieldName === "complianceStatus")?.value?.value?.toUpperCase() &&
				comment === complianceValues?.find((cVal) => cVal.fieldName === "complianceComment")?.value?.value &&
				selectedStatus?.value?.toUpperCase() !== COMPLIANCE_STATUS.NOT_APPLICABLE),
		[comment, selectedStatus, complianceValues]
	);
	useEffect(() => {
		if (open) {
			const status = complianceValues
				?.find((val) => val.fieldName === "complianceStatus")
				?.value?.value?.toUpperCase();
			if (status) {
				const complianceStatus = complianceStatuses?.find((cs) => cs.value === status);
				if (complianceStatus) {
					setSelectedStatus(complianceStatus);
				}
			}
		}
	}, [open, complianceValues, complianceStatuses]);
	const handleSelectStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
		const item = e.target.value as unknown as PossibleValue;
		setSelectedStatus(item);
	};
	const handleChangeComment = (e: React.ChangeEvent<HTMLInputElement>) => {
		setComment(e.target.value);
	};
	const handleSubmit = () => {
		const complianceStatus = compliance.find(({ fieldName }) => fieldName === "complianceStatus");
		const complianceComment = compliance.find(({ fieldName }) => fieldName === "complianceComment");
		if (disabledSubmit || !complianceStatus || !selectedStatus) {
			return;
		}
		try {
			const payload = [
				{
					innerFieldUuid: complianceStatus.fieldUuid,
					value: { valueUuid: selectedStatus.valueUuid, value: selectedStatus.value },
				},
			];
			if (selectedStatus.value.toUpperCase() !== COMPLIANCE_STATUS.NOT_APPLICABLE && complianceComment) {
				payload.push({
					innerFieldUuid: complianceComment.fieldUuid,
					value: { valueUuid: selectedStatus.valueUuid, value: comment },
				});
			}
			updateInnerField({ projectId, fieldUuid: criteriaId }, payload).then((data: Payload) => {
				if (onSubmit && typeof onSubmit === "function") {
					const currentComplianceStatus = complianceValues?.find(
						(val) => val.fieldName === "complianceStatus"
					)?.value?.value;
					onSubmit(criteriaId, data, selectedStatus.value, currentComplianceStatus);
				}
			});
		} catch (err) {
			console.error(err);
		}
	};
	const handleOpenConfirmation = () => {
		setOpenConfirmation((prev) => !prev);
	};
	const handleConfirmation = () => {
		if (selectedStatus?.value?.toUpperCase() === COMPLIANCE_STATUS.NOT_APPLICABLE) {
			handleOpenConfirmation();
		} else {
			handleSubmit();
		}
	};
	const getIcon = (label: string) => {
		const iconName = STATUS_ORDER.find((st) => st.name === label)?.icon;
		if (!iconName) return null;
		return <IconComponent icon={icon[iconName as keyof typeof icon]} size="xs" />;
	};
	const itemRenderer = (label: string) => (
		<div className={styles.complianceItem}>
			<div>{getIcon(label)}</div>
			<div className={styles.complianceTexts}>
				<div
					className={styles.statusLabel}
					data-selected={
						label === (selectedStatus?.value as string) &&
						selectedStatus?.value !== COMPLIANCE_STATUS.NOT_APPLICABLE
					}
				>
					{translate(
						`dashboards.project-sheet.compliance-status.${label.replaceAll("_", "-").toLowerCase()}`
					)}
				</div>
				<div className={styles.statusDescription}>
					{translate(
						`dashboards.project-sheet.compliance-status.${label
							.replaceAll("_", "-")
							.toLowerCase()}.description`
					)}
				</div>
			</div>
		</div>
	);
	const handleOpenRules = () => {
		setOpenRules((prev) => !prev);
	};
	return (
		<>
			<CustomDialog
				className={styles.compliance}
				classNameHeader={styles.complianceHeader}
				open={open}
				submitDisabled={disabledSubmit}
				submitLabel={translate("common:btn.validate")}
				title={translate("dashboards.project-sheet.section-details.compliance.btn.tooltip")}
				onClose={onClose}
				onSubmit={handleConfirmation}
			>
				<div className={styles.blockHeader}>
					{translate("dashboards.project-sheet.section-details.compliance.dialog.compliance")}
					<span className={styles.required}>*</span>
				</div>
				<div className={styles.blockSelector}>
					<Selector
						fullWidth
						field="value"
						itemRenderer={itemRenderer}
						items={complianceStatuses ?? []}
						listClassName={styles.list}
						value={selectedStatus}
						valueRenderer={(item) => itemRenderer((item as PossibleValue).value)}
						onChange={handleSelectStatus}
					/>
				</div>
				{selectedStatus?.value !== COMPLIANCE_STATUS.NOT_APPLICABLE && (
					<>
						<div className={styles.blockHeader}>
							{translate("dashboards.project-sheet.section-details.compliance.dialog.compliance-comment")}
							<span className={styles.required}>*</span>
						</div>
						<div className={styles.blockSelector}>
							<CustomTextField
								fullWidth
								multiline
								className={styles.textField}
								value={comment}
								onChange={handleChangeComment}
							/>
						</div>
					</>
				)}
				<div className={styles.rulesHeader}>
					<CustomIconButton
						icon={icon[(!openRules && "faChevronRight") || "faChevronDown"]}
						size="sm"
						onClick={handleOpenRules}
					/>
					{translate("dashboards.project-sheet.section-details.compliance.dialog.compliance-rules")}
				</div>
				{openRules && <div className={styles.rulesText}>{complianceRules}</div>}
			</CustomDialog>
			<CustomDialog
				classes={{ paper: styles.dialogRoot }}
				iconColor="var(--red-500)"
				iconModel={icon.faTriangleExclamation}
				open={openConfirmation}
				subTitle={translate("dashboards.project-sheet.compliance-status.dialog.confirmation.title")}
				title={translate("dashboards.project-sheet.compliance-status.dialog.confirmation.subtitle")}
				onClose={handleOpenConfirmation}
				onSubmit={handleSubmit}
			/>
		</>
	);
};

export default UpdateComplianceDialog;
